import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  X,
  Minimize2,
  Maximize2,
  Send,
  Paperclip,
  Smile,
  Package,
  Truck,
  CheckSquare,
  Users,
  Search,
  Check,
  CheckCheck,
  Download,
  ExternalLink,
  ChevronRight,
  Reply,
  Loader2,
  Plus,
  Radio,
  FileText,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import { playMessageChime } from './ChatSound';
import EmojiPickerPopover from './EmojiPickerPopover';
import ShareEntityModal from './ShareEntityModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

// Clean legacy corrupted emoji characters (?? and ?) from message strings
const cleanMessageContent = (content) => {
  if (!content) return '';
  return content
    .replace(/^(\?\?|\?)\s*\*\*AI SLA Alert\*\*:/i, '⚠️ **AI SLA Alert**:')
    .replace(/^(\?\?|\?)\s*\*\*LOGI AI Copilot\*\*:/i, '🤖 **LOGI AI Copilot**:')
    .replace(/\?\s*Dest:/g, '➔ Dest:')
    .replace(/Origin:\s*N\/A\s*(\?|->|➔)\s*Dest:\s*N\/A\.?/gi, 'Origin: Central Hub ➔ Dest: Destination Terminal.');
};

export default function ChatDrawer() {
  const navigate = useNavigate();

  // Window state: 'closed' | 'compact' | 'expanded'
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Channels & Active Selection
  const [channels, setChannels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const activeChannel = channels.find(c => c.id === activeChannelId) || null;

  // Messages & Input
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);

  // Search filter inside drawer
  const [channelSearch, setChannelSearch] = useState('');

  // Modals & Popovers
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Real-time & Typing
  const [typingUsers, setTypingUsers] = useState({}); // channel_id -> username
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Current user info from localStorage
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user_details')) || {};
    } catch {
      return {};
    }
  })();
  const currentUserId = currentUser.id || '';

  // ─── 1. FETCH CHANNELS & TEAM ──────────────────────────────────────────────
  const fetchChannels = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/api/chat/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setChannels(data);
        setActiveChannelId(prev => prev || (data.length > 0 ? data[0].id : null));
      }
    } catch (e) {
      console.error('Failed to load chat channels:', e);
    }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/api/chat/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data);
        setOnlineUserIds(new Set(data.filter(u => u.is_online).map(u => u.id)));
      }
    } catch (e) {
      console.error('Failed to load team members:', e);
    }
  }, []);

  // ─── 2. FETCH MESSAGES FOR ACTIVE CHANNEL ──────────────────────────────────
  const fetchMessages = useCallback(async (channelId) => {
    if (!channelId) return;
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/chat/channels/${channelId}/messages?limit=60`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        // Clear unread count locally only if needed to avoid unnecessary re-renders
        setChannels(prev => {
          let hasUnread = false;
          for (const c of prev) {
            if (c.id === channelId && c.unread_count > 0) {
              hasUnread = true;
              break;
            }
          }
          if (!hasUnread) return prev;
          return prev.map(c => c.id === channelId ? { ...c, unread_count: 0 } : c);
        });
      }
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Fetch messages ONLY when activeChannelId changes
  useEffect(() => {
    if (activeChannelId) {
      fetchMessages(activeChannelId);
    }
  }, [activeChannelId, fetchMessages]);

  useEffect(() => {
    fetchChannels();
    fetchTeamMembers();
  }, [fetchChannels, fetchTeamMembers]);

  // Global listener to open chat from anywhere in ERP
  useEffect(() => {
    const handleOpenChat = (e) => {
      setIsOpen(true);
      if (e.detail && e.detail.channelId) {
        setActiveChannelId(e.detail.channelId);
      }
    };
    window.addEventListener('open-chat', handleOpenChat);
    return () => window.removeEventListener('open-chat', handleOpenChat);
  }, []);

  // Scroll to bottom on message change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typingUsers]);

  // ─── 3. WEBSOCKET REAL-TIME CONNECTION ─────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let isDisposed = false;

    const connectWs = () => {
      if (isDisposed) return;
      try {
        ws = new WebSocket(`${WS_URL}/api/chat/ws?token=${encodeURIComponent(token)}`);
        socketRef.current = ws;

        ws.onopen = () => {
          clearInterval(pingTimer);
          pingTimer = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 25000);
        };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_message') {
            const { message, channel_id } = data;

            // If message is in current active channel, append it
            setActiveChannelId(currentActive => {
              if (currentActive === channel_id) {
                setMessages(prev => {
                  if (prev.some(m => m.id === message.id)) return prev;
                  return [...prev, message];
                });
                // Send read status to backend
                fetch(`${BACKEND_URL}/api/chat/channels/${channel_id}/read`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` }
                });
              } else {
                // Increment unread count on channel
                setChannels(prev => prev.map(c => {
                  if (c.id === channel_id) {
                    return { ...c, unread_count: (c.unread_count || 0) + 1, last_message_preview: message.content };
                  }
                  return c;
                }));
              }
              return currentActive;
            });

            // Play notification sound if message from someone else
            if (message.sender_id !== currentUserId) {
              playMessageChime();
            }
          } else if (data.type === 'reaction_update') {
            setMessages(prev => prev.map(m => {
              if (m.id === data.message_id) {
                return { ...m, reactions: data.reactions };
              }
              return m;
            }));
          } else if (data.type === 'presence') {
            setOnlineUserIds(prev => {
              const next = new Set(prev);
              if (data.is_online) next.add(data.user_id);
              else next.delete(data.user_id);
              return next;
            });
          } else if (data.type === 'typing') {
            if (data.is_typing) {
              setTypingUsers(prev => ({ ...prev, [data.channel_id]: data.user_name }));
              setTimeout(() => {
                setTypingUsers(prev => {
                  const copy = { ...prev };
                  delete copy[data.channel_id];
                  return copy;
                });
              }, 3000);
            } else {
              setTypingUsers(prev => {
                const copy = { ...prev };
                delete copy[data.channel_id];
                return copy;
              });
            }
          }
        } catch (err) {
          console.error('Error parsing WS message:', err);
        }
      };

      ws.onclose = () => {
        clearInterval(pingTimer);
        if (!isDisposed) {
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connectWs, 3000);
        }
      };

      ws.onerror = () => {
        // Handled by onclose
      };
    } catch (e) {
      console.warn('WS error:', e);
    }
  };

    connectWs();

    return () => {
      isDisposed = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
    };
  }, [currentUserId]);

  // ─── 4. SEND MESSAGE / UPLOAD / REACT ──────────────────────────────────────
  const handleSendMessage = async (customPayload = null) => {
    if (!activeChannelId) return;

    const payload = customPayload || {
      content: inputText.trim(),
      message_type: 'text',
      reply_to_id: replyingTo ? replyingTo.id : null,
      reply_to_preview: replyingTo ? `${replyingTo.sender_name}: ${replyingTo.content?.slice(0, 50)}` : null
    };

    if (!payload.content && !payload.attachment_path && !payload.metadata) return;

    if (!customPayload) {
      setInputText('');
      setReplyingTo(null);
    }

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/chat/channels/${activeChannelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const sent = await res.json();
        setMessages(prev => {
          if (prev.some(m => m.id === sent.id)) return prev;
          return [...prev, sent];
        });
      }
    } catch (e) {
      console.error('Failed to send message:', e);
    }
  };

  // Broadcast typing indicator
  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN && activeChannelId) {
      socketRef.current.send(JSON.stringify({
        type: 'typing',
        channel_id: activeChannelId,
        user_name: currentUser.full_name || 'Someone',
        is_typing: true
      }));

      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({
            type: 'typing',
            channel_id: activeChannelId,
            user_name: currentUser.full_name || 'Someone',
            is_typing: false
          }));
        }
      }, 1500);
    }
  };

  // Toggle emoji reaction
  const handleToggleReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/chat/messages/${messageId}/react`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ emoji })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
      }
    } catch (e) {
      console.error('Failed to react:', e);
    }
  };

  // Upload file / photo directly to server drive
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/chat/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const fileData = await res.json();
        // Send message with file
        await handleSendMessage({
          content: fileData.message_type === 'image' ? 'Sent a photo' : `Shared file: ${fileData.name}`,
          message_type: fileData.message_type,
          attachment_path: fileData.url,
          attachment_name: fileData.name,
          attachment_size: fileData.size,
          attachment_mime: fileData.mime
        });
      }
    } catch (e) {
      console.error('Upload failed:', e);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Start DM with team member
  const handleStartDM = async (targetUserId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/chat/direct-channel?target_user_id=${targetUserId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const dm = await res.json();
        setChannels(prev => {
          if (prev.some(c => c.id === dm.id)) return prev;
          return [dm, ...prev];
        });
        setActiveChannelId(dm.id);
      }
    } catch (e) {
      console.error('Failed to start DM:', e);
    }
  };

  // Share Docket / Trip / Task Card
  const handleShareEntity = (entity) => {
    handleSendMessage({
      content: `Shared ${entity.type.toUpperCase()}: ${entity.title}`,
      message_type: `${entity.type}_card`,
      metadata: entity.metadata
    });
  };

  // Total unread count across all channels
  const totalUnread = channels.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // Filter channels & team members by search query
  const filteredChannels = channels.filter(c =>
    (c.name || '').toLowerCase().includes(channelSearch.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(channelSearch.toLowerCase())
  );

  return (
    <>
      {/* ─── 1. FLOATING LAUNCHER PILL (ALWAYS VISIBLE BOTTOM-RIGHT) ─── */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 text-white rounded-full shadow-2xl hover:shadow-blue-500/25 hover:scale-105 active:scale-95 transition-all duration-200 border border-white/20 group"
          title="Open Team Chat"
        >
          <div className="relative">
            <MessageSquare className="w-5 h-5 text-white" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 absolute -top-1 -right-1 border-2 border-slate-900 animate-pulse" />
          </div>
          <span className="font-bold text-sm tracking-wide font-manrope">Team Chat</span>
          {totalUnread > 0 && (
            <span className="px-2 py-0.5 text-xs font-black bg-rose-500 text-white rounded-full shadow-sm animate-bounce">
              {totalUnread}
            </span>
          )}
        </button>
      )}

      {/* ─── 2. SLACK-GRADE CHAT DRAWER / WORKSPACE ─── */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ${
            isExpanded ? 'w-[880px] h-[640px]' : 'w-[430px] h-[600px]'
          }`}
        >
          {/* Header Bar */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between border-b border-slate-800">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-sm shadow-sm">
                #
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm truncate tracking-wide">
                    {activeChannel?.name || 'LogisticsHub Team Chat'}
                  </h3>
                  {activeChannel?.type === 'direct' && activeChannel?.other_user && (
                    <span className={`w-2 h-2 rounded-full ${onlineUserIds.has(activeChannel.other_user.id) ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                  )}
                </div>
                <p className="text-[11px] text-slate-400 truncate">
                  {activeChannel?.description || `${onlineUserIds.size} team members online`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-slate-300 hover:text-white transition-colors"
                title={isExpanded ? 'Compact View' : 'Expand View'}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-rose-500/20 text-slate-300 hover:text-rose-400 transition-colors"
                title="Close Chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body: Left Sidebar + Message Area */}
          <div className="flex flex-1 min-h-0 bg-gray-50">
            {/* ─── SIDEBAR: Channels & DMs (Visible always when expanded, or toggleable) ─── */}
            <div className={`${isExpanded ? 'w-64 border-r border-gray-200' : 'hidden'} bg-slate-900/95 text-slate-200 flex flex-col`}>
              {/* Channel search */}
              <div className="p-2.5 border-b border-slate-800">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={channelSearch}
                    onChange={(e) => setChannelSearch(e.target.value)}
                    placeholder="Search channels & people..."
                    className="w-full pl-8 pr-2 py-1.5 bg-slate-800/80 text-xs rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 border border-slate-700"
                  />
                </div>
              </div>

              {/* Scrollable list */}
              <div className="flex-1 overflow-y-auto p-2 space-y-4">
                {/* Public Channels */}
                <div>
                  <div className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                    Channels
                  </div>
                  <div className="space-y-0.5">
                    {filteredChannels.filter(c => c.type === 'channel').map(c => (
                      <button
                        key={c.id}
                        onClick={() => setActiveChannelId(c.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          activeChannelId === c.id
                            ? 'bg-blue-600 text-white font-bold shadow-sm'
                            : 'hover:bg-slate-800 text-slate-300'
                        }`}
                      >
                        <span className="truncate flex items-center gap-1.5">
                          <span className="opacity-70">#</span>
                          {c.raw_name || c.name}
                        </span>
                        {c.unread_count > 0 && (
                          <span className="px-1.5 py-0.2 bg-rose-500 text-white rounded-full text-[10px] font-bold">
                            {c.unread_count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Direct Messages */}
                <div>
                  <div className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center justify-between">
                    <span>Direct Messages</span>
                    <span className="text-[9px] bg-slate-800 px-1 rounded">{teamMembers.length}</span>
                  </div>
                  <div className="space-y-0.5">
                    {teamMembers.map(u => {
                      const isOnline = onlineUserIds.has(u.id);
                      return (
                        <button
                          key={u.id}
                          onClick={() => handleStartDM(u.id)}
                          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-800 text-slate-300 transition-colors group"
                        >
                          <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                          <span className="truncate flex-1 text-left">{u.full_name}</span>
                          <span className="text-[9px] uppercase px-1 rounded bg-slate-800 text-slate-400 group-hover:text-slate-300">
                            {u.role?.split('_')[0]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Logistics Threads (Dockets & Trips) */}
                {filteredChannels.filter(c => ['docket', 'trip', 'task'].includes(c.type)).length > 0 && (
                  <div>
                    <div className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Logistics Threads
                    </div>
                    <div className="space-y-0.5">
                      {filteredChannels.filter(c => ['docket', 'trip', 'task'].includes(c.type)).map(c => (
                        <button
                          key={c.id}
                          onClick={() => setActiveChannelId(c.id)}
                          className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                            activeChannelId === c.id
                              ? 'bg-blue-600 text-white font-bold'
                              : 'hover:bg-slate-800 text-slate-300'
                          }`}
                        >
                          <span className="truncate flex items-center gap-1.5">
                            {c.type === 'docket' ? '📦' : c.type === 'trip' ? '🚚' : '📋'}
                            {c.name}
                          </span>
                          {c.unread_count > 0 && (
                            <span className="px-1.5 py-0.2 bg-rose-500 text-white rounded-full text-[10px]">
                              {c.unread_count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ─── MAIN CHAT AREA ─── */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              {/* Compact Mode: Channel Quick Selector Pills */}
              {!isExpanded && (
                <div className="p-2 bg-slate-100 border-b border-gray-200 flex items-center gap-1.5 overflow-x-auto text-xs shrink-0">
                  {channels.slice(0, 4).map(c => (
                    <button
                      key={c.id}
                      onClick={() => setActiveChannelId(c.id)}
                      className={`px-2.5 py-1 rounded-full whitespace-nowrap font-medium transition-all ${
                        activeChannelId === c.id
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      #{c.raw_name || c.name}
                      {c.unread_count > 0 && ` (${c.unread_count})`}
                    </button>
                  ))}
                  <button
                    onClick={() => setIsExpanded(true)}
                    className="px-2 py-1 text-[11px] text-blue-600 font-bold hover:underline whitespace-nowrap ml-auto"
                  >
                    + More
                  </button>
                </div>
              )}

              {/* Message Feed */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full text-gray-400 gap-2 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    Loading conversation...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center p-6">
                    <MessageSquare className="w-10 h-10 text-gray-300 mb-2" />
                    <p className="font-semibold text-slate-700 text-sm">Welcome to #{activeChannel?.name || 'chat'}</p>
                    <p className="text-xs text-gray-500 max-w-xs mt-1">
                      This is the beginning of the discussion. Send a message, share photos, or link a docket card below!
                    </p>
                  </div>
                ) : (
                  messages.map((m) => {
                    const isMine = m.is_mine;
                    return (
                      <div
                        key={m.id}
                        className={`flex flex-col ${isMine ? 'items-end' : 'items-start'} group`}
                      >
                        {/* Sender info */}
                        <div className="flex items-center gap-1.5 mb-1 px-1 text-[11px] text-gray-500">
                          <span className="font-bold text-slate-700">{m.sender_name}</span>
                          <span className="text-[9px] uppercase px-1.5 py-0.2 bg-gray-100 rounded text-gray-600">
                            {m.sender_role}
                          </span>
                          <span>•</span>
                          <span>{m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>

                        {/* Quoted reply banner */}
                        {m.reply_to_preview && (
                          <div className="text-[11px] bg-gray-100 text-gray-600 border-l-2 border-blue-500 px-2 py-1 rounded mb-1 max-w-xs truncate">
                            Replying: {m.reply_to_preview}
                          </div>
                        )}

                        {/* Message Content Bubble */}
                        <div
                          className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm transition-all ${
                            isMine
                              ? 'bg-blue-600 text-white rounded-br-none'
                              : (m.message_type === 'sla_alert' || (m.content && m.content.includes('AI SLA Alert')))
                              ? 'bg-amber-50/95 text-slate-900 rounded-bl-none border border-amber-200'
                              : 'bg-slate-100 text-slate-800 rounded-bl-none border border-gray-200'
                          }`}
                        >
                          {/* SLA Alert Interactive Card */}
                          {(m.message_type === 'sla_alert' || (m.content && m.content.includes('AI SLA Alert'))) ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between pb-1.5 border-b border-amber-200/80">
                                <div className="flex items-center gap-1.5 font-bold text-xs text-amber-900">
                                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>Critical SLA Warning</span>
                                </div>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-200">
                                  Idle &gt; 24h
                                </span>
                              </div>
                              <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {cleanMessageContent(m.content).replace(/^(⚠️\s*)?\*\*AI SLA Alert\*\*:\s*/i, '')}
                              </p>
                              <button
                                onClick={() => navigate('/dockets')}
                                className="w-full mt-1 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-xs flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                              >
                                <span>Review &amp; Dispatch in ERP</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            /* Standard Message or AI Copilot Content */
                            <div>
                              {m.sender_name === 'LOGI AI Copilot' && (
                                <div className="flex items-center gap-1 text-[11px] font-bold text-purple-700 mb-1 pb-1 border-b border-purple-100">
                                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                                  <span>AI Copilot Intelligence</span>
                                </div>
                              )}
                              {m.content && (
                                <p className="whitespace-pre-wrap leading-relaxed break-words">
                                  {cleanMessageContent(m.content).replace(/^(🤖\s*)?\*\*LOGI AI Copilot\*\*:\s*/i, '')}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Image Attachment with Lightbox */}
                          {m.message_type === 'image' && m.attachment_path && (
                            <div className="mt-2">
                              <img
                                src={`${BACKEND_URL}${m.attachment_path}`}
                                alt={m.attachment_name || 'Attached photo'}
                                onClick={() => setPreviewImage(`${BACKEND_URL}${m.attachment_path}`)}
                                className="max-h-56 rounded-xl cursor-pointer hover:opacity-95 border border-white/20 object-cover shadow-sm transition-transform active:scale-95"
                              />
                            </div>
                          )}

                          {/* File Attachment */}
                          {m.message_type === 'file' && m.attachment_path && (
                            <a
                              href={`${BACKEND_URL}${m.attachment_path}`}
                              target="_blank"
                              rel="noreferrer"
                              download
                              className={`mt-2 flex items-center gap-2 p-2 rounded-xl transition-colors ${
                                isMine ? 'bg-blue-700 hover:bg-blue-800 text-white' : 'bg-white hover:bg-gray-50 border border-gray-200 text-slate-800'
                              }`}
                            >
                              <FileText className="w-4 h-4 shrink-0 text-orange-400" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold truncate">{m.attachment_name || 'Document'}</p>
                                <p className="text-[10px] opacity-75">
                                  {m.attachment_size ? `${(m.attachment_size / 1024).toFixed(1)} KB` : 'Drive File'}
                                </p>
                              </div>
                              <Download className="w-4 h-4 shrink-0 opacity-80" />
                            </a>
                          )}

                          {/* Rich Logistics Card (Docket, Trip, Task) */}
                          {['docket_card', 'trip_card', 'task_card'].includes(m.message_type) && m.metadata && (
                            <div className="mt-2 p-3 bg-white text-slate-800 rounded-xl border border-gray-200 shadow-md">
                              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                                <div className="flex items-center gap-1.5 font-bold text-xs text-blue-700">
                                  {m.message_type === 'docket_card' && <Package className="w-4 h-4 text-orange-500" />}
                                  {m.message_type === 'trip_card' && <Truck className="w-4 h-4 text-emerald-500" />}
                                  {m.message_type === 'task_card' && <CheckSquare className="w-4 h-4 text-indigo-500" />}
                                  <span>{m.metadata.docket_number || m.metadata.trip_number || m.metadata.task_number}</span>
                                </div>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                                  {m.metadata.status || 'Active'}
                                </span>
                              </div>

                              <div className="py-2 text-xs text-gray-600 space-y-1">
                                {m.metadata.origin && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Route:</span>
                                    <span className="font-semibold text-slate-700">
                                      {m.metadata.origin} ➔ {m.metadata.destination}
                                    </span>
                                  </div>
                                )}
                                {m.metadata.weight && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Cargo Weight:</span>
                                    <span className="font-semibold text-slate-700">{m.metadata.weight} kg</span>
                                  </div>
                                )}
                                {m.metadata.driver && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Driver:</span>
                                    <span className="font-semibold text-slate-700">{m.metadata.driver}</span>
                                  </div>
                                )}
                                {m.metadata.assigned_to && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Assigned To:</span>
                                    <span className="font-semibold text-slate-700">{m.metadata.assigned_to}</span>
                                  </div>
                                )}
                              </div>

                              <button
                                onClick={() => {
                                  if (m.message_type === 'docket_card') navigate('/dockets');
                                  else if (m.message_type === 'trip_card') navigate('/dispatch');
                                  else if (m.message_type === 'task_card') navigate('/tasks');
                                }}
                                className="w-full mt-1.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"
                              >
                                <span>Open Details in ERP</span>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Emoji Reactions Row & Quick Action Buttons */}
                        <div className="flex items-center gap-1 mt-1 px-1">
                          {/* Render Reactions */}
                          {m.reactions && Object.keys(m.reactions).map((emoji) => {
                            const voters = m.reactions[emoji] || [];
                            const hasVoted = voters.includes(currentUserId);
                            return (
                              <button
                                key={emoji}
                                onClick={() => handleToggleReaction(m.id, emoji)}
                                className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 border transition-all ${
                                  hasVoted
                                    ? 'bg-blue-50 border-blue-400 text-blue-700 font-bold'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                <span>{emoji}</span>
                                <span className="text-[10px]">{voters.length}</span>
                              </button>
                            );
                          })}

                          {/* Hover action bar */}
                          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 bg-white border border-gray-200 shadow-sm rounded-lg p-0.5 transition-opacity">
                            {['👍', '❤️', '📦', '✅'].map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => handleToggleReaction(m.id, emoji)}
                                className="w-5 h-5 flex items-center justify-center hover:bg-gray-100 rounded text-xs"
                                title={`React ${emoji}`}
                              >
                                {emoji}
                              </button>
                            ))}
                            <button
                              onClick={() => setReplyingTo(m)}
                              className="w-5 h-5 flex items-center justify-center hover:bg-gray-100 rounded text-gray-500"
                              title="Reply"
                            >
                              <Reply className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Typing indicator */}
                {typingUsers[activeChannelId] && (
                  <div className="text-xs text-gray-400 italic flex items-center gap-1.5 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
                    <span>{typingUsers[activeChannelId]} is typing...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Replying banner */}
              {replyingTo && (
                <div className="px-3 py-1.5 bg-blue-50 border-t border-blue-200 flex items-center justify-between text-xs text-blue-900">
                  <span className="truncate">
                    Replying to <b>{replyingTo.sender_name}</b>: "{replyingTo.content?.slice(0, 40)}..."
                  </span>
                  <button onClick={() => setReplyingTo(null)} className="text-blue-500 hover:text-blue-700">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* ─── COMPOSER / INPUT BAR ─── */}
              <div className="p-3 bg-white border-t border-gray-200 relative">
                {/* Emoji Popover */}
                {showEmojiPicker && (
                  <EmojiPickerPopover
                    onSelect={(emoji) => setInputText(prev => prev + emoji)}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                )}

                {/* Uploading progress indicator */}
                {uploadingFile && (
                  <div className="text-xs text-blue-600 flex items-center gap-1 mb-2 font-medium">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Uploading file to server drive...
                  </div>
                )}

                {/* Quick @ai suggestion pills */}
                {inputText.trim().startsWith('@') && (
                  <div className="flex items-center gap-1.5 mb-2 overflow-x-auto pb-1 text-xs">
                    <span className="text-purple-600 font-semibold shrink-0 flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5" /> AI Suggestions:
                    </span>
                    {[
                      '@ai give me updates of my trips',
                      '@ai list idle dockets',
                      '@ai check pending tasks',
                      '@ai what is the cold chain SOP?'
                    ].map((promptText, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setInputText(promptText)}
                        className="px-2.5 py-1 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 shrink-0 font-medium transition-colors"
                      >
                        {promptText.replace('@ai ', '')}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-1.5 bg-gray-50 rounded-2xl p-1.5 border border-gray-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                  {/* Emoji Button */}
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
                    title="Insert Emoji"
                  >
                    <Smile className="w-4 h-4 text-amber-500" />
                  </button>

                  {/* AI Docket Assistant (Option B) */}
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-ai-docket-assistant'))}
                    className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-purple-100 text-purple-600 transition-colors"
                    title="AI Docket Assistant (Option B) - Upload Email Screenshot or Text"
                  >
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </button>

                  {/* Share Docket / Trip Button */}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors"
                    title="Share Docket, Trip or Task"
                  >
                    <Package className="w-4 h-4 text-blue-600" />
                  </button>

                  {/* File / Photo Upload Button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
                    title="Attach File or Image"
                  >
                    <Paperclip className="w-4 h-4 text-gray-600" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                  />

                  {/* Textarea */}
                  <textarea
                    rows={1}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={`Message #${activeChannel?.name || 'chat'}...`}
                    className="flex-1 bg-transparent text-sm resize-none focus:outline-none px-2 max-h-24 py-1 text-slate-800"
                  />

                  {/* Send Button */}
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputText.trim()}
                    className="w-8 h-8 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white flex items-center justify-center transition-transform active:scale-95 shadow-sm"
                    title="Send (Enter)"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── 3. SHARE ENTITY MODAL (DOCKET / TRIP / TASK) ─── */}
      <ShareEntityModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShareEntity}
      />

      {/* ─── 4. LIGHTBOX IMAGE PREVIEW MODAL ─── */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in"
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={previewImage}
              alt="Enlarged view"
              className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-rose-400 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
